import { useFormContext } from 'react-hook-form'
import { ChangeEvent, FC, useEffect } from 'react'
import {Select, SelectItem} from "@heroui/react"
import { DS_TYPOGRAPHY_BODY_SM_REGULAR } from '@shared/ui'

import "./InputSelect.css";

export type InputSelectOptions = {
  name: string
  disabled?: boolean
  value: string | number
}[]

interface InputSelectProps {
  name: string
  options: InputSelectOptions
  hasNoneOption?: boolean
  defaultValue?: undefined | string | number
  disabled?: boolean
  selectClasses?: string
  error?: string
  placeholder?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  label?: string
  size?: 'sm' | 'md'
  className?: string
}

export const InputSelect: FC<InputSelectProps> = ({
  name,
  options,
  hasNoneOption,
  defaultValue,
  disabled,
  error = '',
  placeholder = '',
  onChange,
  label = null,
  size = 'md',
  className = '',
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext()

  const optionsWithKeys = options.map((option) => {
    return {
      ...option,
      key: option.value.toString(),
    }
  })

  const disabledKeys = optionsWithKeys.filter((option) => option.disabled).map((option) => option.key)

  const hasError = errors[name] || error
  const errorMessage = error || (errors[name]?.message as string)

  const selectClasses = [
    DS_TYPOGRAPHY_BODY_SM_REGULAR,
    "qida-input-dropdown",
    `qida-input-dropdown--${size}`,
    hasError ? 'border-danger' : '',
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const currentValue = watch(name)
  
  useEffect(() => {
    // Usar verificación estricta para permitir valor 0 (importante para appointment_modality)
    if (defaultValue !== undefined && currentValue === undefined) {
      setValue(name, defaultValue)
    }
  }, [defaultValue, name, setValue, currentValue])

  const handleSelectionChange = (selectedKeys: any) => {
    const selectedValue = Array.from(selectedKeys)[0]
    if (selectedValue !== undefined) {
      setValue(name, selectedValue)
      if (onChange) {
        const syntheticEvent = {
          target: { value: selectedValue }
        } as ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    }
  }

  const selectedKeys = currentValue !== undefined ? new Set([currentValue.toString()]) : new Set()

  return (
    <div>
        <Select 
          label={label}
          className={selectClasses} 
          isRequired
          isDisabled={disabled} 
          disabledKeys={disabledKeys} 
          placeholder={placeholder}
          disallowEmptySelection
          isInvalid={!!hasError}
          variant="bordered"
          errorMessage={errorMessage}
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          popoverProps={{
            classNames: {
              base: "data-[selectable=true]:focus:bg-transparent",
              content: "qida-input-dropdown-popover",
            },
          }}
          classNames={{ "value": "text-neutral-700 group-data-[has-value=true]:text-neutral-900" }}
        >
          {optionsWithKeys.map((option) => (
            <SelectItem
              key={option.key}
              className='font-aeonik flex justify-between items-center'
            
            >
              {option.name}
            </SelectItem>
          ))}
        </Select>
    </div>
  )
}
